import { IFieldsMap } from "@common/redux/formBuilder/interface";
import {
  FieldProps,
  FormFieldTypes,
  FormFieldTypesNames,
  IBaseFormField,
} from "../FormFieldSelector/interfaces";
import {
  ITableFieldConfigurationClient,
  ITableFieldConfigurationServer,
} from "./interface";
import { IOption } from "@erelego/design-system/components/atoms/DropdownSelect";
import { DataUtility } from "@utils/DataUtility";

export const transformTableFieldConfigClientToServer = (
  fieldConfigClient: ITableFieldConfigurationClient
): ITableFieldConfigurationServer => {
  const {
    initialValue,
    isReference,
    isDatagridCompositeKeyEnabled,
    deleteActionRequired,
    isReferenceByFieldsMap,
    rowActionsRequired,
    isSearchRequired,
    isFirstColumnFreezed,
    iseditModeActionRequired,
    datagridCompositeKeyFields,
    viewType,
    fieldsToShowSum,
    referenceFormId,
    writeJavascript,
    canAdd,
    canDelete,
    minRow,
    maxRow,
    defaultHeight,
    rowCount,
    lookupFilter,
    referenceLookupFilter,
    defaultValue,
    mapFieldsForTotalCount,
  } = fieldConfigClient;
  const fieldConfigServer = {
    initialValue: initialValue,
    isReference: isReference,
    isDatagridCompositeKeyEnabled: isDatagridCompositeKeyEnabled || false,
    deleteActionRequired: deleteActionRequired || false,
    isReferenceByFieldsMap: isReferenceByFieldsMap,
    rowActionsRequired: rowActionsRequired,
    isSearchRequired: isSearchRequired || false,
    isFirstColumnFreezed: isFirstColumnFreezed || false,
    iseditModeActionRequired: iseditModeActionRequired,
    datagridCompositeKeyFields: datagridCompositeKeyFields || [],
    viewType: viewType,
    fieldsToShowSum: fieldsToShowSum || [],
    referenceFormId: referenceFormId,
    writeJavascript: writeJavascript,
    canAdd: canAdd || true,
    canDelete: canDelete || true,
    minRow: minRow ?? 0,
    maxRow: maxRow ?? 10,
    defaultHeight: defaultHeight || "",
    rowCount: {
      isFixedRowCount: rowCount?.isFixedRowCount ?? false,
      count: rowCount?.count ?? 0,
    },
    lookupFilter: lookupFilter,
    referenceLookupFilter: referenceLookupFilter,
    defaultValue: {
      valueType: defaultValue?.valueType,
      tableMapping: {
        selectedForm: defaultValue?.tableMapping?.selectedForm,
        mapFieldsValue: defaultValue?.tableMapping?.mapFieldsValue || [],
      },
      filters: defaultValue?.filters,
      staticJson: defaultValue?.staticJson,
    },
    mapFieldsForTotalCount: mapFieldsForTotalCount || [],
  };
  return fieldConfigServer;
};

export const transformTabletConfigServerToClient = (
  fieldConfigServer: ITableFieldConfigurationServer
): ITableFieldConfigurationClient => {
  const {
    initialValue,
    isDatagridCompositeKeyEnabled,
    deleteActionRequired,
    isReferenceByFieldsMap,
    rowActionsRequired,
    isSearchRequired,
    isFirstColumnFreezed,
    iseditModeActionRequired,
    datagridCompositeKeyFields,
    isReference,
    viewType,
    fieldsToShowSum,
    referenceFormId,
    writeJavascript,
    canAdd,
    canDelete,
    minRow,
    maxRow,
    defaultHeight,
    rowCount,
    lookupFilter,
    referenceLookupFilter,
    defaultValue,
    mapFieldsForTotalCount,
  } = fieldConfigServer;
  const fieldConfigClient = {
    initialValue: initialValue,
    isReference: isReference,
    isReferenceByFieldsMap: isReferenceByFieldsMap,
    isDatagridCompositeKeyEnabled: isDatagridCompositeKeyEnabled || false,
    deleteActionRequired: deleteActionRequired || false,
    rowActionsRequired: rowActionsRequired,
    isSearchRequired:isSearchRequired || false,
    isFirstColumnFreezed: isFirstColumnFreezed || false,
    iseditModeActionRequired: iseditModeActionRequired,
    datagridCompositeKeyFields: datagridCompositeKeyFields || [],
    viewType: viewType,
    fieldsToShowSum: fieldsToShowSum || [],
    referenceFormId: referenceFormId,
    writeJavascript: writeJavascript,
    canAdd: canAdd ?? true,
    canDelete: canDelete ?? true,
    minRow: minRow ?? 0,
    maxRow: maxRow ?? 10,
    defaultHeight: defaultHeight || "",
    rowCount: {
      isFixedRowCount: rowCount?.isFixedRowCount ?? false,
      count: rowCount?.count ?? 0,
    },
    lookupFilter: lookupFilter,
    referenceLookupFilter: referenceLookupFilter,
    defaultValue: {
      valueType: defaultValue?.valueType,
      tableMapping: {
        selectedForm: defaultValue?.tableMapping?.selectedForm,
        mapFieldsValue: defaultValue?.tableMapping?.mapFieldsValue || [],
      },
      filters: defaultValue?.filters,
      staticJson: defaultValue?.staticJson,
    },
    mapFieldsForTotalCount: mapFieldsForTotalCount || [],
  };
  return fieldConfigClient;
};

export const fieldTypesSupportedInDataGrid: string[] = [
  FormFieldTypes["TEXT_INPUT"],
  FormFieldTypes["DROPDOWN"],
  FormFieldTypes["NUMBER_INPUT"],
  FormFieldTypes["DATE_INPUT"],
  FormFieldTypes["TIME_PICKER"],
  FormFieldTypes["DATE_TIME_PICKER"],
  FormFieldTypes["CURRENCY"],
  FormFieldTypes["CHECK_BOX"],
  FormFieldTypes["CALCULATIVE"],
  FormFieldTypes["LOOKUP"],
  FormFieldTypes["TEXT_AREA"],
  FormFieldTypes["FILE_UPLOAD"],
  FormFieldTypes["RATING"],
  FormFieldTypes["BLANK_BOX"],
  FormFieldTypes["FORM_LINK"],
  FormFieldTypes["SEQUENCE"],
  FormFieldTypes["YEAR_SELECTOR"],
  FormFieldTypes["PIN_CODE"],
  FormFieldTypes["BUTTON"],
  FormFieldTypes["MULTIPLE_CHOICES"],
];

export const fieldToSupportTotal: string[] = [
  FormFieldTypes["NUMBER_INPUT"],
  FormFieldTypes["CURRENCY"],
  FormFieldTypes["CALCULATIVE"],
];
export const getTableFieldSelectDropdownOptions = (): IOption[] => {
  const dropdownOptions: IOption[] = [];
  for (let i = 0; i < fieldTypesSupportedInDataGrid?.length; i++) {
    const fieldType = fieldTypesSupportedInDataGrid[i];
    const fieldTypeName = FormFieldTypesNames[fieldType];
    const value = fieldType || "";
    const content = fieldTypeName || "";
    if (value === "" || content === "") {
      continue;
    }
    dropdownOptions.push({ value, content });
  }
  return dropdownOptions;
};

export const getFilteredFieldsMapAndIdsForDataGrid = (
  fieldsMap: IFieldsMap,
  isReferenceField: boolean
) => {
  if (!fieldsMap) return {};
  const filteredFieldsMap: IFieldsMap = {};
  const fieldIds: string[] = [];
  for (const fieldId in fieldsMap) {
    const fieldData = fieldsMap[fieldId];
    const fieldType = fieldData?.fieldType;
    if (fieldTypesSupportedInDataGrid.includes(fieldType)) {
      const updatedFieldData: FieldProps = {
        ...fieldData,
        isReferenceField: isReferenceField || false,
      };
      filteredFieldsMap[fieldId] = updatedFieldData;
      fieldIds.push(fieldId);
    }
  }
  return { filteredFieldsMap, fieldIds: fieldIds };
};

export const getNumberFieldsOptionForCount = (
  fieldsMap: IFieldsMap = {},
  fieldIds: string[] = []
): IOption[] => {
  if (!fieldsMap || fieldIds?.length == 0) {
    return [];
  }
  const fieldSelectOption: IOption[] = [];
  for (const fieldId of fieldIds) {
    const fieldData = fieldsMap[fieldId];
    const fieldType = fieldData?.fieldType;
    if (fieldToSupportTotal?.includes(fieldType)) {
      fieldSelectOption.push({
        content: fieldData?.title,
        value: fieldId,
      });
    }
  }
  fieldSelectOption.push({ value: "rowcount", content: "Row Count" });
  return fieldSelectOption;
};
export const getFieldsOptionForCount = (
  fieldsMap: IFieldsMap = {}
): IOption[] => {
  const fieldIds = Object.keys(fieldsMap ?? {}) ?? [];
  const fieldSelectOption: IOption[] = [];
  for (const fieldId of fieldIds) {
    const fieldData = fieldsMap[fieldId];
    fieldSelectOption.push({
      content: fieldData?.title,
      value: fieldId,
    });
  }
  fieldSelectOption.push({ value: "rowcount", content: "Row Count" });
  return fieldSelectOption;
};

export const checkDeepEqual = (obj1, obj2) => {
  // Check if both objects are of the same type
  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  // If both objects are primitives or null, perform a simple comparison
  if (obj1 === null || typeof obj1 !== "object") {
    return obj1 === obj2;
  }

  // Handle Date and Moment objects accurately using .valueOf()
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }
  if (obj1?._isAMomentObject && obj2?._isAMomentObject) {
    return obj1.valueOf() === obj2.valueOf();
  }

  // Get the keys of both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check if the number of keys is the same
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Check if the keys and their values match recursively
  for (const key of keys1) {
    if (!keys2.includes(key) || !checkDeepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

export const checkIfTotalSubmissionValuesAreUpdated = (
  oldFieldSubmissionMap,
  newFieldSubmissionMap,
  localIds,
  fieldDataPrevState,
  fieldData,
  fieldId
) => {
  for (const columnId in newFieldSubmissionMap) {
    const oldSubmissionData = oldFieldSubmissionMap?.[columnId]?.[fieldId];
    const newubmissionData = newFieldSubmissionMap?.[columnId]?.[fieldId];
    if (!DataUtility.isEqual(oldSubmissionData, newubmissionData)) {
      return true;
    }
  }
  if (fieldDataPrevState && fieldData) {
    if (!DataUtility.isEqual(fieldDataPrevState, fieldData)) {
      return true;
    }
  }
  return false;
};

export const checkIfTotalSubmissionValuesAreUpdated2 = (
  oldFieldSubmissionMap,
  newFieldSubmissionMap,
  fieldId
) => {
  for (const columnId in newFieldSubmissionMap) {
    const oldSubmissionData = oldFieldSubmissionMap?.[columnId]?.[fieldId];
    const newubmissionData = newFieldSubmissionMap?.[columnId]?.[fieldId];
    if (!DataUtility.isEqual(oldSubmissionData, newubmissionData)) {
      return true;
    }
  }
  return false;
};
export const extractFieldIds = (data: Record<string, any>): string[] => {
  const fieldIds: string[] = [];

  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      const item = data[key];

      if (
        item.value &&
        typeof item.value === "object" &&
        item.fieldValueType === "form_field"
      ) {
        if (item.value.value) {
          fieldIds.push(item.value.value);
        }
      }
    }
  }

  return fieldIds;
};
export const hasOrgIdField = (conditionData: Record<string, any>): string => {
  for (const key of Object.keys(conditionData ?? {})) {
    const condition = conditionData[key];

    // Check if the current object has fieldLocalId equal to "orgId"
    if (condition?.fieldLocalId === "orgId") {
      return key;
    }
  }

  return "";
};
export const removeOrgIdConditions = (rules: any[]): any[] => {
  return rules
    .map((rule) => {
      const newRule = { ...rule };
      if (Array.isArray(newRule?.conditions)) {
        newRule.conditions = removeOrgIdConditions(newRule.conditions);
      }
      return newRule;
    })
    .filter((rule) => rule.fieldLocalId !== "orgId");
};
export const getOrgIdValue = (conditions: any[]): string => {
  for (const condition of conditions) {
    if (condition.fieldLocalId === "orgId") {
      return condition.value;
    }

    if (
      Array.isArray(condition.conditions) &&
      condition?.conditions?.length > 0
    ) {
      const nestedValue = getOrgIdValue(condition.conditions);
      if (nestedValue) {
        return nestedValue;
      }
    }
  }

  return "";
};
